import {
    BoltOutlined,
    DirectionsCarOutlined,
    ElectricalServicesOutlined,
    ErrorOutlineOutlined,
    EvStationOutlined,
    PowerOutlined,
} from '@mui/icons-material';
import {FC, ReactNode, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {StatsStrip} from 'src/components/common/StatsStrip';
import {StatsStripTone} from 'src/components/common/StatsStrip/StatsStrip.types';
import {ChargePointStatus, ConnectorStatusEnum} from 'src/enums';
import {ConnectorStatusStripType} from './ConnectorStatusStrip.types';

/**
 * The six statuses an operator acts on, in the order a session moves through them.
 *
 * The enum holds ten. Finishing, Reserved, Unavailable and Undefined are states a connector passes
 * through rather than sits in, and a row of ten numbers answers no question at all. Preparing is not
 * one of those: a cable is in and nothing has authorised it, so it stays there until somebody acts -
 * and it is the state a remote start can be given from, which makes the count actionable rather than
 * merely true. A number that will not come down means people are plugging in and not getting going.
 */
const STRIP_STATUSES: ConnectorStatusEnum[] = [
    ConnectorStatusEnum.AVAILABLE,
    ConnectorStatusEnum.PREPARING,
    ConnectorStatusEnum.CHARGING,
    ConnectorStatusEnum.SUSPENDED_EV,
    ConnectorStatusEnum.SUSPENDED_EVSE,
    ConnectorStatusEnum.FAULTED,
];

const STATUS_ICONS: Record<string, ReactNode> = {
    [ConnectorStatusEnum.AVAILABLE]: <PowerOutlined/>,
    // A cable, because that is the one fact that tells Preparing from Available: plugged in, not yet
    // started. The socket of Available and the bolt of Charging sit either side of it.
    [ConnectorStatusEnum.PREPARING]: <ElectricalServicesOutlined/>,
    [ConnectorStatusEnum.CHARGING]: <BoltOutlined/>,
    [ConnectorStatusEnum.SUSPENDED_EV]: <DirectionsCarOutlined/>,
    [ConnectorStatusEnum.SUSPENDED_EVSE]: <EvStationOutlined/>,
    [ConnectorStatusEnum.FAULTED]: <ErrorOutlineOutlined/>,
};

/** The two suspends are told apart on purpose: who paused the session decides whose job it is. */
const STATUS_TONES: Record<string, StatsStripTone> = {
    [ConnectorStatusEnum.AVAILABLE]: 'success',
    // Waiting, not wrong - and not grey either, which among five coloured tiles reads as no answer.
    [ConnectorStatusEnum.PREPARING]: 'info',
    [ConnectorStatusEnum.CHARGING]: 'brand',
    [ConnectorStatusEnum.SUSPENDED_EV]: 'warning',
    [ConnectorStatusEnum.SUSPENDED_EVSE]: 'secondary',
    [ConnectorStatusEnum.FAULTED]: 'danger',
};

/** Two values, and the order they read in: reachable first, then the ones that are not. */
const STATION_STATUSES: ChargePointStatus[] = [ChargePointStatus.ONLINE, ChargePointStatus.OFFLINE];

export const ConnectorStatusStrip: FC<ConnectorStatusStripType> = ({
                                                                       data,
                                                                       activeStatus,
                                                                       onSelect,
                                                                       actionLabel,
                                                                       activeChargePointStatus,
                                                                       onSelectChargePointStatus,
                                                                   }) => {
    const intl = useIntl();

    // Every status of the enum comes back, zeros included, so a missing point means no answer yet
    // rather than a count of none — the tiles show a dash until the numbers arrive.
    const counts = useMemo(
        () => new Map((data?.points ?? []).map((point) => [point.status, point.connectors])),
        [data]
    );

    const stationCounts = useMemo(
        () => new Map((data?.chargerStatuses ?? []).map((point) => [point.status, point.chargers])),
        [data]
    );

    return (
        <StatsStrip
            icon={<EvStationOutlined/>}
            title="connectorStatusStripTitle"
            subtitle={intl.formatMessage(
                {id: 'connectorStatusStripSubtitle'},
                {connectors: data?.connectors ?? 0, chargers: data?.chargers ?? 0}
            )}
            pills={STATION_STATUSES.map((status) => ({
                key: status,
                label: status,
                value: stationCounts.get(status),
                tone: status === ChargePointStatus.ONLINE ? 'success' : 'neutral',
                active: activeChargePointStatus === status,
                onClick: onSelectChargePointStatus
                    ? () => onSelectChargePointStatus(status)
                    : undefined,
            }))}
            tiles={STRIP_STATUSES.map((status) => ({
                key: status,
                label: status,
                value: counts.get(status),
                icon: STATUS_ICONS[status],
                tone: STATUS_TONES[status],
                emphasise: status === ConnectorStatusEnum.FAULTED,
                active: activeStatus === status,
                actionLabel,
                onClick: onSelect ? () => onSelect(status) : undefined,
            }))}
        />
    );
};
